import { Utils, TicketingFormCategory, MembershipValidityPeriod } from "@simplyk/common";
import { inferRouterInputs } from "@trpc/server";

import { MINIMUM_BID_INCREMENT, mapScheduleToApiFields } from "@/common/helpers/form-edition";
import { AppRouterType } from "@/common/helpers/trpc";
import { TicketingOutput } from "@/common/types/trpc";
import { RateData } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/types";

type EditRateInput = NonNullable<inferRouterInputs<AppRouterType>["updateTicketingRatesAndSeats"]["rates"]>[number];

export const mapDataToEditRates = (rates: RateData[], data: TicketingOutput | undefined): EditRateInput[] => {
  if (!rates || rates.length === 0) {
    return [];
  }

  const registeredRatesById = new Map(data?.rates?.map((registeredRate) => [registeredRate.id, registeredRate]));

  return rates.map((rate, index) => {
    const registeredRate = registeredRatesById.get(rate.id);
    const rateField = registeredRate?.rateFields?.find((rateField) => rateField.locale === data?.locale);
    const eligibleAmount = getEligibleAmountFromRate(rate);
    const isMembershipV2 = data?.formCategory === TicketingFormCategory.MembershipV2;
    const isAuction = data?.formCategory === TicketingFormCategory.Auction;

    const imagesUploaderData = rate.photos;
    const photos: string[] | undefined = imagesUploaderData
      ?.map((imagesUploaderData) => {
        return imagesUploaderData.photoUrl;
      })
      .filter(Utils.notEmpty);

    return {
      id: rate.id,
      amount: rate.amount,
      groupMembershipMaxMembers: rate.isGroupMembership ? Number(rate.groupMembershipMaxMembers) : null,
      attendeesByTicket: rate.isGroup && rate.attendeesByTicket ? parseInt(rate.attendeesByTicket.toString()) : null,
      eligibleAmount,
      seats: parseValueToEditInput(rate.seats),
      minimumToBuy: parseValueToEditInput(rate.rateMinimumNumberToBuy),
      maximumToBuy: rate.rateMaximumNumberToBuy
        ? parseInt(rate.rateMaximumNumberToBuy.toString())
        : isMembershipV2
          ? 1
          : 10,
      membershipValidityPeriod: rate.membershipValidityPeriod as MembershipValidityPeriod,
      membershipValidityPeriodAtDate:
        (rate.membershipValidityPeriod === MembershipValidityPeriod.AtDate &&
          (rate.membershipValidityPeriodAtDate as Date)) ||
        null,
      rateFields: [
        {
          id: rateField?.id,
          title: rate.name,
          description: rate.description,
        },
      ],
      sortIndex: index,
      photos,
      photoUrls: photos,
      ...mapScheduleToApiFields(rate),
      bidIncrement: rate.bidIncrement || MINIMUM_BID_INCREMENT,
      allowAutomaticRenewal: Boolean(rate.allowAutomaticRenewal),
      isPayWhatYouCan: Boolean(rate.isPayWhatYouCan),
      payWhatYouCanMinimumAmount: rate.isPayWhatYouCan ? (rate.payWhatYouCanMinimumAmount ?? null) : null,
      payWhatYouCanSuggestedAmount: rate.isPayWhatYouCan ? (rate.payWhatYouCanSuggestedAmount ?? null) : null,
      isMemberOnly: Boolean(rate.isMemberOnly),
      memberOnlyMembershipRateIds: rate.isMemberOnly ? rate.memberOnlyMembershipRateIds : [],
      isHidden: Boolean(rate.isHidden),
      shopVariants: rate.shopVariants?.map((variant) => ({
        id: variant.id,
        name: variant.name,
        sortIndex: variant.sortIndex,
        type: variant.type,
        values: variant.values.map((value) => ({
          id: value.id,
          name: value.name,
          sortIndex: value.sortIndex,
        })),
      })),
      shopVariantsToDelete: rate.shopVariantsToDelete,
      variantSeats: rate.variantSeats,
      categoryIds: rate.categoryIds,
      taxIds: isAuction || rate.isPayWhatYouCan || (rate.amount ?? 0) > 0 ? rate.taxIds : [],
    };
  });
};

export const getEligibleAmountFromRate = (rate: RateData): number | null => {
  if (!rate.isGeneratingReceipt || !rate.amount || rate.isPayWhatYouCan) {
    return null;
  }
  const receiptEligibleAmount = rate.receiptEligibleAmount ? parseFloat(rate.receiptEligibleAmount.toString()) : 0;
  return receiptEligibleAmount > rate.amount ? rate.amount : Math.floor(receiptEligibleAmount);
};

const parseValueToEditInput = (value: number | null | undefined) => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null || value.toString() === "") {
    return null;
  }
  return parseInt(value.toString());
};
